import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { browserHistory } from 'react-router';
import * as d3 from 'd3';
import styles from './../Layout/Layout.css';
import withStyles from '../../decorators/withStyles';
import { getZoom, simplePlaceName, isBangladesh, isEcuador, isIDB, isUS, xlt, owlSend } from '../../utils/utils';
import {
  BatchActions, SetImpact, SetPlace, SetPlaceName, SetMapPosition, SetStations,
  SetPresetLevels, SetEmbed, SetSSID, SetForecastMetadata, SetWaterLevel, SetForecastType,
  SetPlaceContainer, SetComparisonType, SetMapLayer, SetMapCaption, SetComparisonParent,
  SetDefaultComparison, SetAvailableComparisons, SetChoroplethBounds
} from "../../actions";
import {
  SetASLRStation, SetControlType, SetDownloadFormID, SetEAE, SetForecastName, SetPopulation,
  SetProtection, SetStationNum
} from "../../actions";
import { exampleCounties } from "./utils";


class MetadataProvider extends Component {

  componentWillReceiveProps(nextProps) {
    if (nextProps.params.placeID !== this.props.params.placeID ||
        nextProps.params.placeType !== this.props.params.placeType ||
        nextProps.params.placeID !== this.props.place.id ||
        nextProps.params.placeType !== this.props.place.type) {
      if (nextProps.params.placeType && nextProps.params.placeID) {
        this.loadData(nextProps.params.placeType, nextProps.params.placeID);
      }
    }
  }

  componentWillMount() {
    const placeType = this.props.params.placeType;
    const placeID = this.props.params.placeID;
    if (placeType && placeID) {
      this.loadData(placeType, placeID);
    }
    this.showLanguageSelectors(placeID);
  }

  isEmbedded() {
    const currentLocation = this.props.location.pathname;
    return currentLocation.includes('embed/');
  }

  showLanguageSelectors(placeID) {

    // hide language selector on pages used for slide downloads
    const langSelectorVisibility = this.isEmbedded() ? 'none' : 'block';
    d3.select('#tx-live-lang-container').style('display', langSelectorVisibility);
    
    // limit language selections according to location
    if (placeID) {
      const bnVisibility = isBangladesh(placeID) ? 'block': 'none';
      const esVisibility = (isUS(placeID) || isIDB(placeID)) ? 'block': 'none';
      d3.select("#tx-live-lang-container li[data-value='bn']").style('display', bnVisibility);
      d3.select("#tx-live-lang-container li[data-value='es']").style('display', esVisibility);
    }
  }

  async loadData(placeType, placeID) {
    var self = this;
    
    const urls = [
      `/api/metadata/geo/${placeType}/${placeID}`,
      `/api/metadata/forecast/${placeType}/${placeID}?slrdb=true`,
      `/api/metadata/aslr-flooding`,
      `/api/region_eae/${placeType}/${placeID}`,
    ];

    const results = await Promise.all(urls.map((url) => d3.json(url)));
    const [ data, forecastMeta, aslrStations, eae ] = results;
    // If the default_comparison is undefined, the default comparison is a "peer" view.
    // Set an implicit or explicit "peer" view equal to the placeType.
    // const default_comparison = (data.default_comparison && data.default_comparison !== "peer") ? data.default_comparison
    //   : (placeType === "division" ? "district" : placeType);  // TODO FIXME: remove division -> district once in DB and index.
    data.comparisons.sort((a, b) => a[0] > b[0] ? 1 : -1);
    var default_comparison;
    if (data.comparisons.length > 0) {
      if (data.default_comparison) {
        default_comparison = data.default_comparison === "peer" ? placeType : data.default_comparison;
      } else {
        // Prefer the names of subunits that tend to be large,
        // so that obscure or singular choropleths aren't shown by default.
        const order = new Set(['province', 'region', 'parish']);
        if (data.comparisons[0][0] === "peer") {
          default_comparison = placeType;
        } else {
          default_comparison = (data.comparisons.find(cmp => order.has(cmp[0])) || data.comparisons[0])[0];
        }
      }
    } else {
      owlSend({
        agent: "ssrf-mon",
        entity: "ssrf-metadata",
        message: `No comparisons for ${placeType}/${placeID}`
      });
      default_comparison = placeType;
    }

    var presetLevels;
    var station;
    if (data.stations.length > 0) {
      station = data.stations[0];
      presetLevels = {
        Slow: {level: station.lolev, unit: "ft"},
        Medium: {level: station.lev, unit: "ft"},
        Fast: {level: station.hilev, unit: "ft"}
      };
    } else {
      station = null;
      presetLevels = null;
    }

    const aslrStnSet = new Set(aslrStations.aslr_stations.map(obj => obj['noaa_id']));
    const aslrStn = data.stations.find(stn => aslrStnSet.has(parseInt(stn.id)) && stn.dist < 100);
    if (aslrStn) {
      aslrStn.nws_minor_mhhw = aslrStations.aslr_stations.find(stn => stn['noaa_id'] == aslrStn.id).nws_minor_mhhw;
    }

    // For now, filter out Galveston Bay and Philadelphia stations, which are used for
    // ASLR module, but do not have forecasts, so exclude them from list of selectable stations.
    // FIXME: this information is also encoded in api/forecast.js. The configuration should be consolidated.
    // It is not excluded from api/metadata.js since that's the means by which we locate the ASLR station, as above.
    data.stations = data.stations.filter(stn => stn.id != 8771341 && stn.id != 8545240);
    const wl = {level: this.props.waterLevel.level, unit: isUS(placeID) ? "ft" : "m"};
    if (wl.unit != this.props.waterLevel.unit) {
      if (wl.level === 0.5) {
        wl.level = 1;
      } else if (wl.level === 1.5) {
        wl.level = 4;
      } else if (wl.level === 2.5) {
        wl.level = 8;
      }
    }
    const metadataActions = [];
    if (placeType !== this.props.place.type || placeID !== this.props.place.id) {
      metadataActions.push(SetPlace(placeType, placeID))
    }
    metadataActions.push.apply(metadataActions, [
      SetControlType("water_level"),
      SetWaterLevel(wl),
      SetPlaceName((data.long_name.length >= 45 || window.innerWidth < 600) ? data.name : data.long_name),
      SetSSID(data.ssid),
      SetPopulation(data.population),
      SetStations(data.stations),
      SetASLRStation(aslrStn ? aslrStn : null),   // Ensure "undefined" is not passed to a reducer
      SetEAE(eae),
      SetPresetLevels(presetLevels),
      SetForecastMetadata(forecastMeta),
      SetPlaceContainer(data.geo_container),
      SetDefaultComparison(default_comparison),
      SetAvailableComparisons(data.comparisons),
      SetChoroplethBounds(data.choroplethBounds ? data.choroplethBounds : null)
    ]);

    if (!(data.comparisons.find(cmp => cmp[0] === self.props.comparisonType))) {
      metadataActions.push(SetComparisonType(default_comparison));
    }

    if ("quickpicks" in forecastMeta && forecastMeta.quickpicks.length > 1 && window.location.search.includes("forecastType")) {
      metadataActions.push(SetForecastType(forecastMeta.quickpicks[1]))
    }

    if (this.props.forecastName === "With extreme flood" && !isUS(placeID)) {
      metadataActions.push(SetForecastName("With moderate flood"))
    }

    // Set default water level. Presets for certain places may appear as undefined in the data;
    // Don't set to a preset with an undefined level.
    if (presetLevels && presetLevels.Medium.level && window.location.search.indexOf("level") === -1) {
      metadataActions.push(SetWaterLevel(presetLevels.Medium))
    }

    if (placeID && !isUS(placeID) && !isEcuador(placeID)) {
      metadataActions.push(SetProtection("unprotected"));
    }

    if (placeID && isEcuador(placeID)) {
      metadataActions.push(SetImpact("Population_Landscan10"));
    }

    if (placeID && !isUS(placeID)) {
      metadataActions.push(SetMapLayer("satellite"));
      if (isBangladesh(placeID)) {
        metadataActions.push(SetWaterLevel({level: 2, unit: "m"}));
      } else {
        metadataActions.push(SetWaterLevel({level: 1, unit: "m"}));
      }
    } else {
      metadataActions.push(SetMapLayer("sovi"));
    }

    const findCounty = (data) => {
      var biggestCounty = null;
      Object.keys(data).forEach((k) => {
        if (data[k].population && (!biggestCounty || data[k].population > data[biggestCounty].population)) {
          biggestCounty = k;
        }
      });
      return biggestCounty;
    };

    // Note: this code was designed to randomly chose between different possible
    //    versions of the download form, for use in A/B testing, for instance.
    //    At present only one form version is being used.
    //
    // const dlFormID = Math.round(Math.random()) ? "columns-ad" : "no-columns-ad";
    metadataActions.push(SetDownloadFormID("no-columns-ad"));

    const getCountyData = async (county) => {
      const countyData = await d3.json(`/api/metadata/geo/county/${county}`);
      const mapCaption = <span><var>{xlt(simplePlaceName(countyData.name))}</var>, a highly affected area in <var>{xlt(countyData.geo_container.name)}</var>.</span>;
      self.props.dispatch(BatchActions(
        SetMapPosition(
          countyData.lat,
          countyData.lon,
          getZoom(countyData.lat, countyData.lon, countyData.bounds.split(","))
        ),
        SetMapCaption(mapCaption)
      ));
    };

    if (placeType === "state") {
      if (placeID in exampleCounties) {
        const county = exampleCounties[placeID];
        getCountyData(county);
      } else {
        const data = await d3.json(`/api/metadata/geo/county?container=${placeID}`);
        getCountyData(findCounty(data));
      }
    } else {
      metadataActions.push(SetMapPosition(data.lat, data.lon, getZoom(data.lat, data.lon, data.bounds.split(","))));
      metadataActions.push(SetMapCaption(""));
    }

    self.props.dispatch(BatchActions(...metadataActions));

    if (data.comparison_parents.length > 0 && data.comparison_parents[0]) {
      const [ parentPlacetype, parentPlaceID ] = data.comparison_parents[0].split("/");
      const metadata = await d3.json(`/api/metadata/geo/${parentPlacetype}/${parentPlaceID}`);
      self.props.dispatch(SetComparisonParent(metadata.name));
    } else {
      // If a place is top-level and has a null container name, use its own name.
      self.props.dispatch(SetComparisonParent(data.geo_container.name || data.name));
    }
  }

  render() {
    return (
     <div>
       {this.props.children}
     </div>
    )
  }

}

export default compose(
  withStyles(styles),
  connect((state) => state),
)(MetadataProvider);
